// Hooks TanStack Query do módulo Notifications (M1 §8 "Data") — mesma técnica preview do módulo Workflow.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../../../lib/api/apiClientContext';
import { previewRequest } from '../../../lib/api/previewRequest';
import {
  type MessageTemplateSummary,
  type MessageTemplateVersion,
  type NotificationPreferences,
  type TestSendRequest,
  NOTIFICATIONS_PREVIEW_PATHS,
} from './contract.preview';

export function useTemplates() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'notification-templates'],
    queryFn: () =>
      previewRequest<{ items: readonly MessageTemplateSummary[] }>(
        client,
        'get',
        NOTIFICATIONS_PREVIEW_PATHS.templates,
      ),
    meta: { silent: true },
  });
}

export interface TemplateDetail extends MessageTemplateSummary {
  readonly versions: readonly MessageTemplateVersion[];
}

export function useTemplate(id: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'notification-templates', id],
    queryFn: () =>
      previewRequest<TemplateDetail>(client, 'get', NOTIFICATIONS_PREVIEW_PATHS.template(id)),
    enabled: Boolean(id),
    meta: { silent: true },
  });
}

function useInvalidateTemplate(id: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['network', 'notification-templates'] });
    void queryClient.invalidateQueries({ queryKey: ['network', 'notification-templates', id] });
  };
}

export interface SaveTemplateVersionInput {
  readonly templateId: string;
  readonly version: number;
  readonly locales: MessageTemplateVersion['locales'];
  readonly changeReason: string;
}

export function useSaveTemplateVersion() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveTemplateVersionInput) =>
      previewRequest<MessageTemplateVersion>(
        client,
        'put',
        NOTIFICATIONS_PREVIEW_PATHS.version(input.templateId, input.version),
        { body: { locales: input.locales, changeReason: input.changeReason } },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['network', 'notification-templates', variables.templateId],
      });
    },
    meta: { silent: true },
  });
}

export function useSubmitTemplate(templateId: string) {
  const client = useApiClient();
  const invalidate = useInvalidateTemplate(templateId);
  return useMutation({
    mutationFn: () =>
      previewRequest<MessageTemplateVersion>(
        client,
        'post',
        NOTIFICATIONS_PREVIEW_PATHS.submit(templateId),
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface ApproveTemplateInput {
  readonly meaning: string;
  readonly comment?: string;
}

export function useApproveTemplate(templateId: string) {
  const client = useApiClient();
  const invalidate = useInvalidateTemplate(templateId);
  return useMutation({
    mutationFn: (input: ApproveTemplateInput) =>
      previewRequest<MessageTemplateVersion>(
        client,
        'post',
        NOTIFICATIONS_PREVIEW_PATHS.approve(templateId),
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useRetireTemplate(templateId: string) {
  const client = useApiClient();
  const invalidate = useInvalidateTemplate(templateId);
  return useMutation({
    mutationFn: () =>
      previewRequest(client, 'post', NOTIFICATIONS_PREVIEW_PATHS.retire(templateId)),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useTestSendTemplate(templateId: string) {
  const client = useApiClient();
  return useMutation({
    mutationFn: (input: TestSendRequest) =>
      previewRequest<{ sent: boolean }>(
        client,
        'post',
        NOTIFICATIONS_PREVIEW_PATHS.testSend(templateId),
        { body: input },
      ),
    meta: { silent: true },
  });
}

export function useNotificationPreferences() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['me', 'notification-preferences'],
    queryFn: () =>
      previewRequest<NotificationPreferences>(
        client,
        'get',
        NOTIFICATIONS_PREVIEW_PATHS.preferences,
      ),
    meta: { silent: true },
  });
}

export function useSaveNotificationPreferences() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      input: Omit<NotificationPreferences, 'etag' | 'smsAvailable' | 'mandatoryCategories'>,
    ) =>
      previewRequest<NotificationPreferences>(
        client,
        'put',
        NOTIFICATIONS_PREVIEW_PATHS.preferences,
        { body: input },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['me', 'notification-preferences'], data);
    },
    meta: { silent: true },
  });
}
